import "./CostiCarburante.css";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select, { components } from "react-select";
import Lf23Loader from "../../../components/Lf23Loader";

// componente custom per limitare visualizzazione valori selezionati
const ValueContainer = ({ children, ...props }) => {
    const { getValue, hasValue } = props;

    if (!hasValue) {
        return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
    }

    const values = getValue();
    const numValues = values.length;

    // Mostra solo i primi 2 valori + badge
    const childrenArray = React.Children.toArray(children);
    const inputChild = childrenArray.find(child =>
        child && child.type && child.type.name === 'Input'
    );

    const multiValueChildren = childrenArray.filter(child =>
        child && child.props && child.props.data
    );

    const limitedChildren = [
        ...multiValueChildren.slice(0, 2),
        numValues > 2 && (
            <div key="badge" className="costi-carburante-select-more-badge">
                +{numValues - 2}
            </div>
        ),
        inputChild
    ].filter(Boolean);

    return (
        <components.ValueContainer {...props}>
            {limitedChildren}
        </components.ValueContainer>
    );
};

const CostiCarburante = ({ filters, loading: parentLoading = false }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // opzioni per i dropdown
    const colonneOptions = [
        { value: 'anno', label: 'Anno' },
        { value: 'trimestre', label: 'Trimestre' },
        { value: 'mese', label: 'Mese' },
    ];

    const righeOptions = [
        { value: 'cantiere', label: 'Cantiere' },
        { value: 'sottotipologia', label: 'Sottotipologia' },
        { value: 'targa', label: 'Targa' },
        { value: 'settore', label: 'Settore' },
        { value: 'commessa', label: 'Commessa' },
        { value: 'attivita_livello_1', label: 'Attività Livello 1' },
        { value: 'attivita_livello_2', label: 'Attività Livello 2' },
        { value: 'attivita_livello_3', label: 'Attività Livello 3' },
        { value: 'attivita_livello_4', label: 'Attività Livello 4' },
    ];

    const indicatoreOptions = [
        { value: 'volume_totale', label: 'Volume Totale' },
        { value: 'costo_litro', label: 'Costo/Litro' },
        { value: 'km_percorsi', label: 'Km Percorsi' },
        { value: 'km_per_litro', label: 'Km per Litro' },
    ];

    // stati per i dropdown
    const [selectedColonne, setSelectedColonne] = useState([colonneOptions.find(opt => opt.value === 'mese')]);
    const [selectedRighe, setSelectedRighe] = useState([]);
    const [selectedIndicatore, setSelectedIndicatore] = useState([...indicatoreOptions]);

    // stato per i dati del backend
    const [datiBackend, setDatiBackend] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);

    // stato per righe espanse
    const [expandedRows, setExpandedRows] = useState([]);

    const [isFullscreenTable, setIsFullscreenTable] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsFullscreenTable(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);


    // Fetch dati quando cambiano i filtri
    useEffect(() => {
        const fetchDati = async () => {
            setDataLoading(true);
            try {
                const params = new URLSearchParams();

                // aggiungo i filtri
                if (filters?.fromDate) params.append("fromDate", filters.fromDate);
                if (filters?.toDate) params.append("toDate", filters.toDate);
                if (filters?.targa?.length > 0) {
                    params.append("targa", filters.targa.map(t => t.value).join(","));
                }
                if (filters?.prodotto?.length > 0) {
                    params.append("prodotto", filters.prodotto.map(p => p.value).join(","));
                }
                if (filters?.distributore?.length > 0) {
                    params.append("distributore", filters.distributore.map(d => d.value).join(","));
                }
                if (filters?.cantiere?.length > 0) {
                    params.append("cantiere", filters.cantiere.map(c => c.value).join(","));
                }
                if (filters?.tipologia?.length > 0) {
                    params.append("tipologia", filters.tipologia.map(t => t.value).join(","));
                }
                if (filters?.settore?.length > 0) {
                    params.append("settore", filters.settore.map(s => s.value).join(","));
                }
                if (filters?.commessa?.length > 0) {
                    params.append("commessa", filters.commessa.map(c => c.value).join(","));
                }

                const url = `${API_URL}/trans-automezzi/costi-carburante?${params.toString()}`;
                const response = await axios.get(url);
                setDatiBackend(response.data);
            } catch (error) {
                console.error("Errore caricamento dati:", error);
                setDatiBackend([]);
            } finally {
                setDataLoading(false);
            }
        };

        if (filters) {
            fetchDati();
        }
    }, [filters, API_URL]);

    //lista anni disèponibli
    const anniDisponibili = useMemo(() => {
        if (!datiBackend || datiBackend.length === 0) return [];
        const anni = [...new Set(datiBackend.map(d => String(d.anno)))].sort();
        return anni;
    }, [datiBackend]);

    //mesi disponibili per ogni anno
    const mesiDisponibili = useMemo(() => {
        if (!datiBackend || datiBackend.length === 0) return {};
        const mesiPerAnno = {};
        datiBackend.forEach(d => {
            const anno = String(d.anno);
            const mese = Number(d.mese);
            if (!mesiPerAnno[anno]) {
                mesiPerAnno[anno] = new Set();
            }
            mesiPerAnno[anno].add(mese);
        });
        // Converto i Set in array ordinati
        Object.keys(mesiPerAnno).forEach(anno => {
            mesiPerAnno[anno] = [...mesiPerAnno[anno]].sort((a, b) => a - b);
        });
        return mesiPerAnno;
    }, [datiBackend]);

    const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

    // Mappa mese -> trimestre
    const meseToTrimestre = {
        1: 1, 2: 1, 3: 1,
        4: 2, 5: 2, 6: 2,
        7: 3, 8: 3, 9: 3,
        10: 4, 11: 4, 12: 4
    };

    // Verifica se un trimestre ha dati
    const isTrimestreValido = (anno, trimestre) => {
        const mesiAnno = mesiDisponibili[anno] || [];
        return mesiAnno.some(m => meseToTrimestre[m] === trimestre);
    };

    // Verifica se un mese ha dati
    const isMeseValido = (anno, mese) => {
        const mesiAnno = mesiDisponibili[anno] || [];
        return mesiAnno.includes(mese);
    };

    // PERIODI DA MOSTRARE (colonne della tabella)
    const getPeriodiDaMostrare = (datiTrasformati = []) => {
        // Estraggo tutti i periodi presenti nei dati trasformati
        const periodiPresenti = new Set();

        const aggiungiPeriodi = (elemento) => {
            Object.keys(elemento.periodi).forEach(p => periodiPresenti.add(p));
            if (elemento.children) {
                elemento.children.forEach(child => aggiungiPeriodi(child));
            }
        };

        datiTrasformati.forEach(elemento => aggiungiPeriodi(elemento));

        if (selectedColonne.length === 0) {
            return anniDisponibili.map(anno => ({
                chiave: anno,
                etichetta: anno,
                anno: anno
            })).concat([{ chiave: 'totale', etichetta: 'Totale', anno: 'totale' }]);
        }

        let periodi = [];
        const haTrimestreSelezionato = selectedColonne.some(opt => opt.value === 'trimestre');
        const haMeseSelezionato = selectedColonne.some(opt => opt.value === 'mese');
        const haAnnoSelezionato = selectedColonne.some(opt => opt.value === 'anno');

        for (let i = 0; i < anniDisponibili.length; i++) {
            const anno = anniDisponibili[i];

            if (haMeseSelezionato) {
                for (let m = 1; m <= 12; m++) {
                    const meseNumero = String(m).padStart(2, '0');
                    const chiaveMese = `${anno}-${meseNumero}`;

                    // Mostro solo i mesi che hanno effettivamente dati
                    if (periodiPresenti.has(chiaveMese)) {
                        periodi.push({
                            chiave: chiaveMese,
                            etichetta: nomiMesi[m - 1],
                            anno: anno
                        });
                    }
                }
            } else if (haTrimestreSelezionato) {
                for (let t = 1; t <= 4; t++) {
                    const chiaveTrim = `${anno}-trim${t}`;
                    if (periodiPresenti.has(chiaveTrim)) {
                        periodi.push({ chiave: chiaveTrim, etichetta: `T${t}`, anno: anno });
                    }
                }
            } else if (haAnnoSelezionato) {
                if (periodiPresenti.has(anno)) {
                    periodi.push({
                        chiave: anno,
                        etichetta: anno,
                        anno: anno
                    });
                }
            }
        }

        periodi.push({ chiave: 'totale', etichetta: 'Totale', anno: 'totale' });
        return periodi;
    };

    // Toggle espansione righe
    const toggleRowExpansion = (rowKey) => {
        if (expandedRows.includes(rowKey)) {
            setExpandedRows(expandedRows.filter(id => id !== rowKey));
        } else {
            setExpandedRows([...expandedRows, rowKey]);
        }
    };

    const isRowExpanded = (rowKey) => expandedRows.includes(rowKey);

    // Formattazione numeri
    const formatNumber = (num, decimals = 2) => {
        if (num === null || num === undefined || isNaN(num)) return "-";
        return new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num);
    };

    // FUNZIONE PER TRASFORMARE I DATI (tabella gerarchica)
    const trasformaDati = (datiGrezzi) => {
        // Determino i campi di raggruppamento (max 4 livelli)
        let campoLivello1 = 'settore';
        let campoLivello2 = null;
        let campoLivello3 = null;
        let campoLivello4 = null;

        if (selectedRighe.length > 0) campoLivello1 = selectedRighe[0].value;
        if (selectedRighe.length > 1) campoLivello2 = selectedRighe[1].value;
        if (selectedRighe.length > 2) campoLivello3 = selectedRighe[2].value;
        if (selectedRighe.length > 3) campoLivello4 = selectedRighe[3].value;

        const raggruppati = {};

        const creaPeriodoVuoto = () => ({
            volume_totale: 0,
            costo_litro_pesato: 0, 
            km_percorsi: 0,
            km_per_litro: 0
        });

        // Raggruppo i dati per livelli
        for (let i = 0; i < datiGrezzi.length; i++) {
            const riga = datiGrezzi[i];

            const valore1 = riga[campoLivello1] || 'Non assegnato';
            const valore2 = campoLivello2 ? (riga[campoLivello2] || 'Non assegnato') : null;
            const valore3 = campoLivello3 ? (riga[campoLivello3] || 'Non assegnato') : null;
            const valore4 = campoLivello4 ? (riga[campoLivello4] || 'Non assegnato') : null;

            const volume = Number(riga.volume_totale) || 0;
            const costoLitro = Number(riga.costo_litro) || 0;
            const km = Number(riga.km_percorsi) || 0;

            const meseNumero = String(riga.mese).padStart(2, '0');
            const chiavePeriodo = `${riga.anno}-${meseNumero}`;

            // Livello 1 (padre)
            if (!raggruppati[valore1]) {
                raggruppati[valore1] = {
                    id: valore1,
                    label: valore1,
                    periodi: {},
                    children: [],
                    childrenMap: {}
                };
            }

            if (!campoLivello2) {
                if (!raggruppati[valore1].periodi[chiavePeriodo]) {
                    raggruppati[valore1].periodi[chiavePeriodo] = creaPeriodoVuoto();
                }
                raggruppati[valore1].periodi[chiavePeriodo].volume_totale += volume;
                raggruppati[valore1].periodi[chiavePeriodo].costo_litro_pesato += costoLitro * volume;
                raggruppati[valore1].periodi[chiavePeriodo].km_percorsi += km;
            } else {
                // Livello 2 (figlio)
                if (!raggruppati[valore1].childrenMap[valore2]) {
                    raggruppati[valore1].childrenMap[valore2] = {
                        id: `${valore1}-${valore2}`,
                        label: valore2,
                        periodi: {},
                        children: [],
                        childrenMap: {}
                    };
                }

                const figlio = raggruppati[valore1].childrenMap[valore2];

                if (!campoLivello3) {
                    if (!figlio.periodi[chiavePeriodo]) {
                        figlio.periodi[chiavePeriodo] = creaPeriodoVuoto();
                    }
                    figlio.periodi[chiavePeriodo].volume_totale += volume;
                    figlio.periodi[chiavePeriodo].costo_litro_pesato += costoLitro * volume;
                    figlio.periodi[chiavePeriodo].km_percorsi += km;
                } else {
                    // Livello 3 (nipote)
                    if (!figlio.childrenMap[valore3]) {
                        figlio.childrenMap[valore3] = {
                            id: `${valore1}-${valore2}-${valore3}`,
                            label: valore3,
                            periodi: {},
                            children: [],
                            childrenMap: {}
                        };
                    }

                    const nipote = figlio.childrenMap[valore3];

                    if (!campoLivello4) {
                        if (!nipote.periodi[chiavePeriodo]) {
                            nipote.periodi[chiavePeriodo] = creaPeriodoVuoto();
                        }
                        nipote.periodi[chiavePeriodo].volume_totale += volume;
                        nipote.periodi[chiavePeriodo].costo_litro_pesato += costoLitro * volume;
                        nipote.periodi[chiavePeriodo].km_percorsi += km;
                    } else {
                        // Livello 4 (pronipote)
                        if (!nipote.childrenMap[valore4]) {
                            nipote.childrenMap[valore4] = {
                                id: `${valore1}-${valore2}-${valore3}-${valore4}`,
                                label: valore4,
                                periodi: {},
                                children: []
                            };
                        }

                        const pronipote = nipote.childrenMap[valore4];

                        if (!pronipote.periodi[chiavePeriodo]) {
                            pronipote.periodi[chiavePeriodo] = creaPeriodoVuoto();
                        }
                        pronipote.periodi[chiavePeriodo].volume_totale += volume;
                        pronipote.periodi[chiavePeriodo].costo_litro_pesato += costoLitro * volume;
                        pronipote.periodi[chiavePeriodo].km_percorsi += km;
                    }
                }
            }
        }

        // somma dai figli ai padri
        const sommaPeriodiFigli = (padre) => {
            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];
                const periodiFiglio = Object.keys(figlio.periodi);

                for (let k = 0; k < periodiFiglio.length; k++) {
                    const chiave = periodiFiglio[k];
                    const dati = figlio.periodi[chiave];

                    if (!padre.periodi[chiave]) {
                        padre.periodi[chiave] = creaPeriodoVuoto();
                    }

                    padre.periodi[chiave].volume_totale += dati.volume_totale;
                    padre.periodi[chiave].costo_litro_pesato += dati.costo_litro_pesato || 0;
                    padre.periodi[chiave].km_percorsi += dati.km_percorsi;
                }
            }
        };

        // Converto childrenMap in array e sommo
        const padri = Object.keys(raggruppati);
        for (let i = 0; i < padri.length; i++) {
            const padre = raggruppati[padri[i]];

            padre.children = Object.values(padre.childrenMap || {});
            delete padre.childrenMap;

            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];

                figlio.children = Object.values(figlio.childrenMap || {});
                delete figlio.childrenMap;

                for (let k = 0; k < figlio.children.length; k++) {
                    const nipote = figlio.children[k];

                    nipote.children = Object.values(nipote.childrenMap || {});
                    delete nipote.childrenMap;

                    if (nipote.children.length > 0) {
                        sommaPeriodiFigli(nipote);
                    }
                }

                if (figlio.children.length > 0) {
                    sommaPeriodiFigli(figlio);
                }
            }

            if (padre.children.length > 0) {
                sommaPeriodiFigli(padre);
            }
        }

        // Calcolo aggregati (trimestre, anno, totale)
        const meseToTrimestreMap = {
            '01': 'trim1', '02': 'trim1', '03': 'trim1',
            '04': 'trim2', '05': 'trim2', '06': 'trim2',
            '07': 'trim3', '08': 'trim3', '09': 'trim3',
            '10': 'trim4', '11': 'trim4', '12': 'trim4'
        };

        const calcolaAggregati = (elemento) => {
            const trimestri = {};
            const anni = {};
            let totale = { volume_totale: 0, costo_litro_pesato: 0, km_percorsi: 0 };

            const periodiMensili = Object.keys(elemento.periodi);
            for (let j = 0; j < periodiMensili.length; j++) {
                const chiave = periodiMensili[j];
                const periodo = elemento.periodi[chiave];

                if (chiave.match(/^\d{4}-\d{2}$/)) {
                    // Calcolo costo_litro e km_per_litro per il mese
                    if (periodo.volume_totale > 0) {
                        periodo.costo_litro = periodo.costo_litro_pesato / periodo.volume_totale;
                        periodo.km_per_litro = periodo.km_percorsi / periodo.volume_totale;
                    } else {
                        periodo.costo_litro = 0;
                        periodo.km_per_litro = 0;
                    }

                    const parti = chiave.split('-');
                    const anno = parti[0];
                    const mese = parti[1];
                    const trimestre = meseToTrimestreMap[mese];
                    const chiaveTrimestre = `${anno}-${trimestre}`;

                    // Accumulo trimestre
                    if (!trimestri[chiaveTrimestre]) {
                        trimestri[chiaveTrimestre] = { volume_totale: 0, costo_litro_pesato: 0, km_percorsi: 0 };
                    }
                    trimestri[chiaveTrimestre].volume_totale += periodo.volume_totale;
                    trimestri[chiaveTrimestre].costo_litro_pesato += periodo.costo_litro_pesato || 0;
                    trimestri[chiaveTrimestre].km_percorsi += periodo.km_percorsi;

                    // Accumulo anno
                    if (!anni[anno]) {
                        anni[anno] = { volume_totale: 0, costo_litro_pesato: 0, km_percorsi: 0 };
                    }
                    anni[anno].volume_totale += periodo.volume_totale;
                    anni[anno].costo_litro_pesato += periodo.costo_litro_pesato || 0;
                    anni[anno].km_percorsi += periodo.km_percorsi;

                    // Accumulo totale
                    totale.volume_totale += periodo.volume_totale;
                    totale.costo_litro_pesato += periodo.costo_litro_pesato || 0;
                    totale.km_percorsi += periodo.km_percorsi;
                }
            }

            // Aggiungo trimestri
            const chiaveTrimestri = Object.keys(trimestri);
            for (let j = 0; j < chiaveTrimestri.length; j++) {
                const chiave = chiaveTrimestri[j];
                const trim = trimestri[chiave];
                elemento.periodi[chiave] = {
                    volume_totale: trim.volume_totale,
                    costo_litro: trim.volume_totale > 0 ? trim.costo_litro_pesato / trim.volume_totale : 0,
                    km_percorsi: trim.km_percorsi,
                    km_per_litro: trim.volume_totale > 0 ? trim.km_percorsi / trim.volume_totale : 0
                };
            }

            // Aggiungo anni
            const chiaveAnni = Object.keys(anni);
            for (let j = 0; j < chiaveAnni.length; j++) {
                const chiave = chiaveAnni[j];
                const annoData = anni[chiave];
                elemento.periodi[chiave] = {
                    volume_totale: annoData.volume_totale,
                    costo_litro: annoData.volume_totale > 0 ? annoData.costo_litro_pesato / annoData.volume_totale : 0,
                    km_percorsi: annoData.km_percorsi,
                    km_per_litro: annoData.volume_totale > 0 ? annoData.km_percorsi / annoData.volume_totale : 0
                };
            }

            // Aggiungo totale
            elemento.periodi['totale'] = {
                volume_totale: totale.volume_totale,
                costo_litro: totale.volume_totale > 0 ? totale.costo_litro_pesato / totale.volume_totale : 0,
                km_percorsi: totale.km_percorsi,
                km_per_litro: totale.volume_totale > 0 ? totale.km_percorsi / totale.volume_totale : 0
            };
        };

        // Applico aggregati a tutti i livelli
        const listaPadri = Object.keys(raggruppati);
        for (let i = 0; i < listaPadri.length; i++) {
            const padre = raggruppati[listaPadri[i]];

            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];

                for (let k = 0; k < figlio.children.length; k++) {
                    const nipote = figlio.children[k];

                    for (let l = 0; l < nipote.children.length; l++) {
                        calcolaAggregati(nipote.children[l]);
                    }

                    calcolaAggregati(nipote);
                }

                calcolaAggregati(figlio);
            }

            calcolaAggregati(padre);
        }

        return Object.values(raggruppati);
    };

    // RENDERING HEADER TABELLA
    const renderTableHeader = (datiTrasformati) => {
        const periodi = getPeriodiDaMostrare(datiTrasformati);
        const haAnnoSelezionato = selectedColonne.some(c => c.value === 'anno');
        const haTrimestreSelezionato = selectedColonne.some(c => c.value === 'trimestre');
        const haMeseSelezionato = selectedColonne.some(c => c.value === 'mese');

        // Mesi che appartengono a ciascun trimestre
        const mesiPerTrimestre = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };

        // Calcolo quante colonne per periodo
        const colonnePerPeriodo = selectedIndicatore.length;

        return (
            <thead>
                {/* RIGA ANNO */}
                {haAnnoSelezionato && (
                    <tr>
                        <th className="th-settore">Anno</th>
                        {(() => {
                            let celle = [];

                            for (let i = 0; i < anniDisponibili.length; i++) {
                                const anno = anniDisponibili[i];
                                let colSpanAnno = colonnePerPeriodo;

                                if (haMeseSelezionato) {
                                    let mesiValidi = 0;
                                    for (let m = 1; m <= 12; m++) {
                                        if (isMeseValido(anno, m)) mesiValidi++;
                                    }
                                    colSpanAnno = mesiValidi * colonnePerPeriodo;
                                } else if (haTrimestreSelezionato) {
                                    let trimValidi = 0;
                                    for (let t = 1; t <= 4; t++) {
                                        if (isTrimestreValido(anno, t)) trimValidi++;
                                    }
                                    colSpanAnno = trimValidi * colonnePerPeriodo;
                                }

                                if (colSpanAnno > 0) {
                                    celle.push(
                                        <th key={anno} colSpan={colSpanAnno} className="th-periodo">{anno}</th>
                                    );
                                }
                            }

                            let rowSpanTotale = 1;
                            if (haTrimestreSelezionato) rowSpanTotale++;
                            if (haMeseSelezionato) rowSpanTotale++;

                            celle.push(
                                <th
                                    key="totale"
                                    colSpan={colonnePerPeriodo}
                                    rowSpan={rowSpanTotale}
                                    className="th-periodo th-totale-header"
                                >
                                    Totale
                                </th>
                            );

                            return celle;
                        })()}
                    </tr>
                )}

                {/* RIGA TRIMESTRE */}
                {haTrimestreSelezionato && (
                    <tr>
                        <th className="th-settore">Trimestre</th>
                        {(() => {
                            let celle = [];

                            const getColSpanTrimestre = (anno, trim) => {
                                if (!haMeseSelezionato) return colonnePerPeriodo;
                                const mesiValidi = mesiPerTrimestre[trim].filter(m => isMeseValido(anno, m)).length;
                                return mesiValidi * colonnePerPeriodo;
                            };

                            for (let i = 0; i < anniDisponibili.length; i++) {
                                const anno = anniDisponibili[i];
                                for (let t = 1; t <= 4; t++) {
                                    if (isTrimestreValido(anno, t)) {
                                        celle.push(
                                            <th key={`${anno}-t${t}`} colSpan={getColSpanTrimestre(anno, t)} className="th-periodo">
                                                Q{t} {anno}
                                            </th>
                                        );
                                    }
                                }
                            }

                            if (!haAnnoSelezionato) {
                                let rowSpanTotale = 1;
                                if (haMeseSelezionato) rowSpanTotale++;
                                celle.push(
                                    <th
                                        key="totale"
                                        colSpan={colonnePerPeriodo}
                                        rowSpan={rowSpanTotale}
                                        className="th-periodo th-totale-header"
                                    >
                                        Totale
                                    </th>
                                );
                            }

                            return celle;
                        })()}
                    </tr>
                )}

                {/* RIGA MESE */}
                {haMeseSelezionato && (
                    <tr>
                        <th className="th-settore">Mese</th>
                        {(() => {
                            let celle = [];

                            for (let i = 0; i < anniDisponibili.length; i++) {
                                const anno = anniDisponibili[i];
                                for (let m = 0; m < 12; m++) {
                                    if (isMeseValido(anno, m + 1)) {
                                        celle.push(
                                            <th key={`${anno}-${m}`} colSpan={colonnePerPeriodo} className="th-periodo">
                                                {nomiMesi[m]} {anno}
                                            </th>
                                        );
                                    }
                                }
                            }

                            if (!haAnnoSelezionato && !haTrimestreSelezionato) {
                                celle.push(
                                    <th
                                        key="totale"
                                        colSpan={colonnePerPeriodo}
                                        className="th-periodo th-totale-header"
                                    >
                                        Totale
                                    </th>
                                );
                            }

                            return celle;
                        })()}
                    </tr>
                )}

                {/* RIGA INDICATORI */}
                <tr>
                    <th className="th-settore">Settore</th>
                    {periodi.map(periodo => {
                        const classeTh = periodo.chiave === 'totale'
                            ? 'th-metrica th-metrica-totale'
                            : 'th-metrica';

                        return selectedIndicatore.map(ind => (
                            <th key={`${periodo.chiave}-${ind.value}`} className={classeTh}>
                                {ind.label}
                            </th>
                        ));
                    })}
                </tr>
            </thead>
        );
    };

    // RENDERING RIGHE TABELLA
    const renderTableRows = (datiTrasformati, periodi) => {

        const renderRigaLivello = (elemento, livello, indentazione) => {
            const hasChildren = elemento.children && elemento.children.length > 0;
            const isExpanded = isRowExpanded(elemento.id);

            let icona = '•';
            if (hasChildren) {
                icona = isExpanded ? '▼' : '▶';
            }

            let classeSettore = `td-settore td-indent-${indentazione}`;
            if (hasChildren) {
                classeSettore = `td-settore td-indent-${indentazione} td-expandable`;
            }

            return (
                <React.Fragment key={elemento.id}>
                    <tr className={`tr-level-${livello}`}>
                        <td
                            className={classeSettore}
                            onClick={() => {
                                if (hasChildren) {
                                    toggleRowExpansion(elemento.id);
                                }
                            }}
                        >
                            <span className="expand-icon">{icona}</span>
                            <span className="level-value">{elemento.label}</span>
                        </td>
                        {periodi.map(periodo =>
                            selectedIndicatore.map(ind => {
                                const valore = elemento.periodi[periodo.chiave]?.[ind.value] || 0;
                                return (
                                    <td key={`${periodo.chiave}-${ind.value}`}>
                                        {formatNumber(valore)}
                                    </td>
                                );
                            })
                        )}
                    </tr>

                    {hasChildren && isExpanded && elemento.children.map(figlio =>
                        renderRigaLivello(figlio, livello + 1, indentazione + 1)
                    )}
                </React.Fragment>
            );
        };

        return datiTrasformati.map(elemento => renderRigaLivello(elemento, 1, 0));
    };

    // RENDERING FOOTER (totali)
    const renderTableFooter = (datiTrasformati, periodi) => {
        const totali = { periodi: {} };

        datiTrasformati.forEach(elemento => {
            Object.keys(elemento.periodi).forEach(periodo => {
                if (!totali.periodi[periodo]) {
                    totali.periodi[periodo] = {
                        volume_totale: 0,
                        costo_litro_pesato: 0,
                        km_percorsi: 0
                    };
                }
                const dest = totali.periodi[periodo];
                const src = elemento.periodi[periodo];

                dest.volume_totale += src.volume_totale || 0;
                dest.costo_litro_pesato += (src.costo_litro || 0) * (src.volume_totale || 0);
                dest.km_percorsi += src.km_percorsi || 0;
            });
        });

        // Calcolo costo_litro e km_per_litro per i totali
        Object.keys(totali.periodi).forEach(periodo => {
            const p = totali.periodi[periodo];
            p.costo_litro = p.volume_totale > 0 ? p.costo_litro_pesato / p.volume_totale : 0;
            p.km_per_litro = p.volume_totale > 0 ? p.km_percorsi / p.volume_totale : 0;
        });

        return (
            <tr className="tr-totali">
                <td className="td-settore"><strong>Totale</strong></td>
                {periodi.map(periodo =>
                    selectedIndicatore.map(ind => {
                        const valore = totali.periodi[periodo.chiave]?.[ind.value] || 0;
                        return (
                            <td key={`totale-${periodo.chiave}-${ind.value}`}>
                                <strong>{formatNumber(valore)}</strong>
                            </td>
                        );
                    })
                )}
            </tr>
        );
    };

    // FUNZIONE PER PREPARARE I DATI DA ESPORTARE
    const prepareExportData = (datiTrasformati, periodi) => {
        if (!datiTrasformati || datiTrasformati.length === 0) {
            return null;
        }

        // 1- creo le intestazioni delle colonne
        const headers = [selectedRighe.map(r => r.label).join(' > ')]; 

        // Per ogni periodo, aggiungo le colonne degli indicatori visibili
        for (let i = 0; i < periodi.length; i++) {
            const periodo = periodi[i];

            selectedIndicatore.forEach(ind => {
                headers.push(`${periodo.etichetta} - ${ind.label}`);
            });
        }

        // 2- creo le righe con i dati
        const rows = [];

        // Funzione helper per aggiungere una riga
        const aggiungiRiga = (label, periodiNodo, livello = 0) => {
            const riga = ['  '.repeat(livello) + label]; 

            // Per ogni periodo (dell'array globale), aggiungo i valori degli indicatori
            for (let i = 0; i < periodi.length; i++) {
                const periodo = periodi[i];
                const datiPeriodo = periodiNodo[periodo.chiave] || {};

                selectedIndicatore.forEach(ind => {
                    const valore = datiPeriodo[ind.value] || 0;
                    riga.push(typeof valore === 'number' ? valore.toFixed(2) : valore);
                });
            }

            rows.push(riga);
        };

        // 3- ciclo su tutti i dati e aggiungo le righe (tutti i livelli)
        const aggiungiGerarchicamente = (nodi, livello = 0) => {
            for (let i = 0; i < nodi.length; i++) {
                const nodo = nodi[i];
                aggiungiRiga(nodo.label, nodo.periodi, livello);

                if (nodo.children && nodo.children.length > 0) {
                    aggiungiGerarchicamente(nodo.children, livello + 1);
                }
            }
        };

        aggiungiGerarchicamente(datiTrasformati);

        // 4- aggiungo la riga TOTALE
        // Volume e km sono additivi; costo_litro e km_per_litro sono medie pesate
        // sui volumi (stessa logica di renderTableFooter), NON somme dei rapporti.
        const totaliPerPeriodo = {};

        for (let i = 0; i < periodi.length; i++) {
            const periodo = periodi[i];
            let volTot = 0, costoPesato = 0, kmTot = 0;

            for (let j = 0; j < datiTrasformati.length; j++) {
                const datiPeriodo = datiTrasformati[j].periodi[periodo.chiave] || {};
                const vol = datiPeriodo.volume_totale || 0;
                volTot += vol;
                costoPesato += (datiPeriodo.costo_litro || 0) * vol;
                kmTot += datiPeriodo.km_percorsi || 0;
            }

            totaliPerPeriodo[periodo.chiave] = {
                volume_totale: volTot,
                costo_litro: volTot > 0 ? costoPesato / volTot : 0,
                km_percorsi: kmTot,
                km_per_litro: volTot > 0 ? kmTot / volTot : 0,
            };
        }

        aggiungiRiga('TOTALE', totaliPerPeriodo, 0);

        return { headers, rows };
    };

    // FUNZIONE PER ESPORTARE IN EXCEL
    const handleExportExcel = async (datiTrasformati, periodi) => {
        try {
            const datiDaEsportare = prepareExportData(datiTrasformati, periodi);

            if (!datiDaEsportare) {
                console.error("Nessun dato da esportare");
                return;
            }

            const response = await axios.post(`${API_URL}/trans-automezzi/downloadExcel`,
                {
                    tableName: 'Costi Carburante',
                    datiDaStampare: datiDaEsportare
                },
                { responseType: "blob" }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "costi_carburante.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante l'export:", error);
        }
    };

    // stati di visualizzazione
    const isLoading = parentLoading || dataLoading;
    const isEmpty = !isLoading && datiBackend.length === 0;
    const hasData = !isLoading && datiBackend.length > 0;

    return (
        <div className="costi-carburante-container">
            {isLoading && (
                <Lf23Loader />
            )}

            {isEmpty && (
                <div className="costi-carburante-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <p>Nessun dato disponibile per i filtri selezionati</p>
                </div>
            )}

            {hasData && (() => {
                const datiTrasformati = trasformaDati(datiBackend);
                const periodi = getPeriodiDaMostrare(datiTrasformati);

                return (
                    <div className={`costi-carburante-table-section${isFullscreenTable ? ' fullscreen-costi' : ''}`}>
                        <RotateDevice active={isFullscreenTable} />
                        {/* HEADER CON TITOLO E BOTTONE EXPORT */}
                        <div className="costi-carburante-table-header">
                            <span className="costi-carburante-table-title">
                                Costi Carburante per Settore, Commessa, Livello Attività e Targa
                            </span>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                    className="costi-carburante-download-btn"
                                    onClick={() => handleExportExcel(datiTrasformati, periodi)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>
                                <button
                                    className="costi-carburante-fullscreen-btn"
                                    onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                    title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenTable ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* DROPDOWN CONFIGURAZIONE */}
                        <div className="costi-carburante-config-row">
                            <div className="costi-carburante-config-item">
                                <label>COLONNE</label>
                                <Select
                                    isMulti
                                    value={selectedColonne}
                                    onChange={setSelectedColonne}
                                    options={colonneOptions}
                                    placeholder="Selezioni multiple"
                                    classNamePrefix="costi-carburante-select"
                                    components={{ ValueContainer }}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>

                            <div className="costi-carburante-config-item">
                                <label>RIGHE</label>
                                <Select
                                    isMulti
                                    value={selectedRighe}
                                    onChange={setSelectedRighe}
                                    options={righeOptions}
                                    placeholder="Selezioni multiple"
                                    classNamePrefix="costi-carburante-select"
                                    components={{ ValueContainer }}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>

                            <div className="costi-carburante-config-item">
                                <label>INDICATORE</label>
                                <Select
                                    isMulti
                                    value={selectedIndicatore}
                                    onChange={setSelectedIndicatore}
                                    options={indicatoreOptions}
                                    placeholder="Tutte"
                                    classNamePrefix="costi-carburante-select"
                                    components={{ ValueContainer }}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>
                        </div>

                        {/* TABELLA*/}
                        <div className="costi-carburante-table-wrapper">
                            <table className="costi-carburante-table">
                                {renderTableHeader(datiTrasformati)}
                                <tbody>
                                    {renderTableRows(datiTrasformati, periodi)}
                                    {renderTableFooter(datiTrasformati, periodi)}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })()}

        </div>
    );
};

export default CostiCarburante;